import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { formations } from './formationData';
import { setFormation, changeFormationKeepPlayers } from '../../store/teamSlice';
import FormationChangeDialog from './FormationChangeDialog';
import './FormationSelector.css';

const FormationSelector: React.FC = () => {
  const dispatch = useDispatch();
  const currentFormation = useSelector((state: RootState) => state.team.team.formation);
  const teamPlayers = useSelector((state: RootState) => state.team.team.players);
  const substitutes = useSelector((state: RootState) => state.team.team.substitutes);
  
  // State for formation change dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingFormation, setPendingFormation] = useState<string | null>(null);

  // Modified to show dialog if there are players in the team
  const handleFormationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const formationId = e.target.value;
    const selectedFormation = formations.find(f => f.id === formationId);
    
    if (selectedFormation) {
      // If there are players in the team, show the dialog
      if (Object.values(teamPlayers).some(player => player !== null) || substitutes.length > 0) {
        setPendingFormation(formationId);
        setDialogOpen(true);
      } else {
        // If no players, just change formation directly
        dispatch(setFormation(selectedFormation));
      }
    }
  };
  
  // Apply formation change with player mapping
  const handleKeepPlayers = () => {
    if (pendingFormation) {
      const selectedFormation = formations.find(f => f.id === pendingFormation);
      if (selectedFormation) {
        dispatch(changeFormationKeepPlayers(selectedFormation));
      }
    }
    // Close dialog
    setDialogOpen(false);
    setPendingFormation(null);
  };
  
  // Apply formation change without players
  const handleStartNew = () => {
    if (pendingFormation) {
      const selectedFormation = formations.find(f => f.id === pendingFormation);
      if (selectedFormation) {
        dispatch(setFormation(selectedFormation));
      }
    }
    // Close dialog
    setDialogOpen(false);
    setPendingFormation(null);
  };
  
  // Cancel formation change
  const handleCancel = () => {
    setDialogOpen(false);
    setPendingFormation(null);
  };

  // Get formation name for the dialog
  const getPendingFormationName = (): string => {
    if (!pendingFormation) return '';
    const formation = formations.find(f => f.id === pendingFormation);
    return formation ? formation.name : '';
  };

  return (
    <div className="formation-selector">
      <div className="formation-header">
        <h3>Formation</h3>
        <select 
          className="formation-dropdown"
          value={currentFormation.id}
          onChange={handleFormationChange}
        >
          {formations.map((formation) => (
            <option key={formation.id} value={formation.id}>
              {formation.name}
            </option>
          ))}
        </select>
      </div>

      <div className="formation-preview">
        <div className="formation-visual">
          <div className="formation-grid">
            {currentFormation.positions.map((position, index) => (
              <div 
                key={`${position}-${index}`} 
                className="formation-dot" 
                data-position={position}
                style={{
                  gridArea: getGridArea(position, currentFormation.id),
                }}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Formation change dialog */}
      <FormationChangeDialog
        isOpen={dialogOpen}
        newFormationName={getPendingFormationName()}
        onKeepPlayers={handleKeepPlayers}
        onStartNew={handleStartNew}
        onCancel={handleCancel}
      />
    </div>
  );
};

// Helper function to define grid areas for formation dots
const getGridArea = (position: string, formationId: string): string => {
  // For goalkeepers, always at the bottom
  if (position === 'GK') return '5 / 3 / 6 / 4';
  
  // Basic positioning logic based on formation and position
  switch (formationId) {
    case '4-4-2':
      if (position === 'LB') return '4 / 1 / 5 / 2';
      if (position === 'LCB') return '4 / 2 / 5 / 3';
      if (position === 'RCB') return '4 / 4 / 5 / 5';
      if (position === 'RB') return '4 / 5 / 5 / 6';
      if (position === 'LM') return '3 / 1 / 4 / 2';
      if (position === 'LCM') return '3 / 2 / 4 / 3';
      if (position === 'RCM') return '3 / 4 / 4 / 5';
      if (position === 'RM') return '3 / 5 / 4 / 6';
      if (position === 'LST') return '1 / 2 / 2 / 3';
      if (position === 'RST') return '1 / 4 / 2 / 5';
      break;
      
    case '4-3-3':
      if (position === 'LB') return '4 / 1 / 5 / 2';
      if (position === 'LCB') return '4 / 2 / 5 / 3';
      if (position === 'RCB') return '4 / 4 / 5 / 5';
      if (position === 'RB') return '4 / 5 / 5 / 6';
      if (position === 'CDM') return '3 / 3 / 4 / 4';
      if (position === 'LCM') return '3 / 2 / 4 / 3';
      if (position === 'RCM') return '3 / 4 / 4 / 5';
      if (position === 'LW') return '1 / 1 / 2 / 2';
      if (position === 'ST') return '1 / 3 / 2 / 4';
      if (position === 'RW') return '1 / 5 / 2 / 6';
      break;
      
    case '3-5-2':
      if (position === 'LCB') return '4 / 1 / 5 / 2';
      if (position === 'CCB') return '4 / 3 / 5 / 4';
      if (position === 'RCB') return '4 / 5 / 5 / 6';
      if (position === 'LWB') return '3 / 1 / 4 / 2';
      if (position === 'LCM') return '3 / 2 / 4 / 3';
      if (position === 'CDM') return '3 / 3 / 4 / 4';
      if (position === 'RCM') return '3 / 4 / 4 / 5';
      if (position === 'RWB') return '3 / 5 / 4 / 6';
      if (position === 'LST') return '1 / 2 / 2 / 3';
      if (position === 'RST') return '1 / 4 / 2 / 5';
      break;
      
    case '4-2-3-1':
      if (position === 'LB') return '4 / 1 / 5 / 2';
      if (position === 'LCB') return '4 / 2 / 5 / 3';
      if (position === 'RCB') return '4 / 4 / 5 / 5';
      if (position === 'RB') return '4 / 5 / 5 / 6';
      if (position === 'LCDM') return '3 / 2 / 4 / 3';
      if (position === 'RCDM') return '3 / 4 / 4 / 5';
      if (position === 'LW') return '2 / 1 / 3 / 2';
      if (position === 'CAM') return '2 / 3 / 3 / 4';
      if (position === 'RW') return '2 / 5 / 3 / 6';
      if (position === 'ST') return '1 / 3 / 2 / 4';
      break;
      
    case '3-4-3':
      if (position === 'LCB') return '4 / 1 / 5 / 2';
      if (position === 'CCB') return '4 / 3 / 5 / 4';
      if (position === 'RCB') return '4 / 5 / 5 / 6';
      if (position === 'LM') return '3 / 1 / 4 / 2';
      if (position === 'LCM') return '3 / 2 / 4 / 3';
      if (position === 'RCM') return '3 / 4 / 4 / 5';
      if (position === 'RM') return '3 / 5 / 4 / 6';
      if (position === 'LW') return '1 / 1 / 2 / 2';
      if (position === 'ST') return '1 / 3 / 2 / 4';
      if (position === 'RW') return '1 / 5 / 2 / 6';
      break;
      
    case '5-3-2':
      if (position === 'LWB') return '4 / 1 / 5 / 2';
      if (position === 'LCB') return '4 / 2 / 5 / 3';
      if (position === 'CCB') return '4 / 3 / 5 / 4';
      if (position === 'RCB') return '4 / 4 / 5 / 5';
      if (position === 'RWB') return '4 / 5 / 5 / 6';
      if (position === 'LCM') return '3 / 2 / 4 / 3';
      if (position === 'CCM') return '3 / 3 / 4 / 4';
      if (position === 'RCM') return '3 / 4 / 4 / 5';
      if (position === 'LST') return '1 / 2 / 2 / 3';
      if (position === 'RST') return '1 / 4 / 2 / 5';
      break;
  }
  
  // Default positioning based on position type if not explicitly defined
  // These are fallbacks and should rarely be used with the comprehensive mapping above
  if (position.includes('B')) return '4 / 3 / 5 / 4'; // Defender
  if (position.includes('M')) return '3 / 3 / 4 / 4'; // Midfielder
  if (position.includes('W') || position.includes('F') || position === 'ST') return '1 / 3 / 2 / 4'; // Attacker
  
  return '3 / 3 / 4 / 4'; // Default center
};

export default FormationSelector; 